/**
 * ConnectorSettingsDrawer
 * Right-side slide-out drawer for connector configuration
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2, CheckCircle, XCircle, Key, Shield } from 'lucide-react';
import ConfirmDialog from '../shared/ConfirmDialog';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import {
  testConnection,
  updateConnector,
  pauseConnector,
} from '../../services/connectorService';

const SYNC_INTERVALS = [
  { label: '5m', value: 5 },
  { label: '15m', value: 15 },
  { label: '30m', value: 30 },
  { label: '1h', value: 60 },
  { label: '6h', value: 360 },
];

const DATA_TYPE_OPTIONS = ['call', 'chat', 'email'];

const ANALYTICS_OPTIONS = [
  { key: 'sentiment', label: 'Sentiment Analysis', desc: 'Analyze customer sentiment and emotions' },
  { key: 'qa', label: 'QA Report', desc: 'Quality assurance scoring per interaction' },
  { key: 'escalation', label: 'Escalation Prediction', desc: 'Predict escalation risk level' },
  { key: 'location', label: 'Location Extraction', desc: 'Extract geographic data from conversations' },
];

const ConnectorSettingsDrawer = ({ isOpen, connector, connectorTypes = [], onClose, onConnectorUpdated }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [syncInterval, setSyncInterval] = useState(15);
  const [startDate, setStartDate] = useState('');
  const [dataTypes, setDataTypes] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [analyticsConfig, setAnalyticsConfig] = useState({ sentiment: true, qa: true, escalation: true, location: true });
  const [editingCredentials, setEditingCredentials] = useState(false);
  const [newCredentials, setNewCredentials] = useState({});
  const [savingCredentials, setSavingCredentials] = useState(false);
  const drawerRef = useRef(null);

  // Initialize form from connector data
  useEffect(() => {
    if (connector) {
      setDisplayName(connector.display_name || '');
      setSyncInterval(connector.poll_interval_minutes || 15);
      setStartDate(connector.config?.start_date || '');
      const types = {};
      DATA_TYPE_OPTIONS.forEach(t => {
        types[t] = connector.config?.data_types?.includes(t) || false;
      });
      setDataTypes(types);
      const ac = connector.config?.analytics_config || {};
      setAnalyticsConfig({
        sentiment: ac.sentiment !== false,
        qa: ac.qa !== false,
        escalation: ac.escalation !== false,
        location: ac.location !== false,
      });
      setEditingCredentials(false);
      setNewCredentials({});
    }
  }, [connector]);

  // Slide-in animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Focus trap and Escape key
  const handleKeyDown = useCallback((e) => {
    if (showDisconnectConfirm) return;

    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'Tab' && drawerRef.current) {
      const focusable = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose, showDisconnectConfirm]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !connector) return null;

  const handleTestConnection = async () => {
    setTesting(true);
    const result = await testConnection(connector.id);
    setTesting(false);
    if (result.success) {
      toast.success('Connection test passed');
    } else {
      toast.error(result.error || 'Connection test failed');
    }
  };

  const connectorTypeDef = connectorTypes.find(t => t.connector_type === connector.connector_type);
  const credentialFields = connectorTypeDef?.credential_fields || [];

  const handleSaveCredentials = async () => {
    setSavingCredentials(true);
    const result = await updateConnector(connector.id, { credentials: newCredentials });
    if (result.success) {
      const testResult = await testConnection(connector.id);
      if (testResult.success) {
        toast.success('Credentials updated and verified');
      } else {
        toast.error('Credentials saved but connection test failed');
      }
      setEditingCredentials(false);
      setNewCredentials({});
      onConnectorUpdated();
    } else {
      toast.error(result.error || 'Failed to update credentials');
    }
    setSavingCredentials(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      display_name: displayName,
      poll_interval_minutes: syncInterval,
      config: {
        ...connector.config,
        start_date: startDate || undefined,
        data_types: Object.entries(dataTypes)
          .filter(([, enabled]) => enabled)
          .map(([type]) => type),
        analytics_config: analyticsConfig,
      },
    };
    const result = await updateConnector(connector.id, payload);
    setSaving(false);
    if (result.success) {
      toast.success('Settings saved');
      onConnectorUpdated();
    } else {
      toast.error(result.error || 'Failed to save settings');
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    const result = await pauseConnector(connector.id);
    setDisconnecting(false);
    setShowDisconnectConfirm(false);
    if (result.success) {
      toast.success('Connector disconnected');
      onClose();
      onConnectorUpdated();
    } else {
      toast.error(result.error || 'Failed to disconnect');
    }
  };

  const handleDataTypeToggle = (type) => {
    setDataTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const statusDot = connector.status === 'active'
    ? 'bg-green-500'
    : connector.status === 'error'
      ? 'bg-red-500'
      : 'bg-gray-400';

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col transition-transform duration-300 ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{connector.display_name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close settings"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Section A: Connection */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Connection</h3>
            <div className="space-y-3">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <div className="flex items-center space-x-2">
                  <span className={`w-2 h-2 rounded-full ${statusDot}`} />
                  <span className="text-sm font-medium text-gray-900 capitalize">{connector.status}</span>
                </div>
              </div>

              {/* API Key (masked) */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Key size={14} className="text-gray-400" />
                  <span className="text-sm text-gray-700">API Key</span>
                </div>
                <span className="text-sm text-gray-400 font-mono tracking-wider">{'•'.repeat(12)}</span>
              </div>

              {/* Actions — every one of these is a connector_service write
                  (analytics:connectors). The drawer itself opens on a read, so
                  a viewer can inspect settings but not change them. The
                  credential form below is reachable only via Re-authenticate,
                  so gating this block covers Save & Verify too. */}
              <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
              <div className="flex items-center space-x-2 pt-1">
                {!editingCredentials && (
                  <button
                    onClick={() => {
                      const defaults = {};
                      credentialFields.forEach(f => { defaults[f.name] = ''; });
                      setNewCredentials(defaults);
                      setEditingCredentials(true);
                    }}
                    disabled={credentialFields.length === 0}
                    className="flex items-center space-x-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    <Shield size={12} />
                    <span>Re-authenticate</span>
                  </button>
                )}
                <button
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="flex items-center space-x-1.5 text-xs text-primary-600 border border-primary-300 rounded-lg px-3 py-1.5 hover:bg-primary-50 disabled:opacity-50 transition-colors"
                >
                  {testing ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <CheckCircle size={12} />
                  )}
                  <span>{testing ? 'Testing...' : 'Test Connection'}</span>
                </button>
              </div>
              </PermissionGate>

              {/* Credential edit form */}
              {editingCredentials && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                  <p className="text-xs font-medium text-gray-700">Enter new credentials</p>
                  {credentialFields.map(field => (
                    <div key={field.name}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type={field.field_type === 'password' ? 'password' : 'text'}
                        placeholder={field.placeholder}
                        value={newCredentials[field.name] || ''}
                        onChange={(e) => setNewCredentials(prev => ({ ...prev, [field.name]: e.target.value }))}
                        aria-required={field.required || false}
                        className="input text-sm"
                      />
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={handleSaveCredentials}
                      disabled={savingCredentials || !credentialFields.filter(f => f.required).every(f => newCredentials[f.name]?.trim())}
                      className="flex items-center space-x-1.5 text-xs text-white bg-primary-600 rounded-lg px-3 py-1.5 hover:bg-primary-700 disabled:opacity-50 transition-colors"
                    >
                      {savingCredentials ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                      <span>{savingCredentials ? 'Saving...' : 'Save & Verify'}</span>
                    </button>
                    <button
                      onClick={() => { setEditingCredentials(false); setNewCredentials({}); }}
                      className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section B: Configuration */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Configuration</h3>
            <div className="space-y-4">
              {/* Display Name */}
              <div>
                <label htmlFor="drawer-display-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  id="drawer-display-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="input"
                />
              </div>

              {/* Sync Interval */}
              <div>
                <label htmlFor="drawer-sync-interval" className="block text-sm font-medium text-gray-700 mb-1">
                  Sync Interval
                </label>
                <select
                  id="drawer-sync-interval"
                  value={syncInterval}
                  onChange={(e) => setSyncInterval(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {SYNC_INTERVALS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label htmlFor="drawer-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  id="drawer-start-date"
                  type="date"
                  value={startDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input"
                />
                <p className="text-[10px] text-gray-400 mt-1">Import interactions from this date onward.</p>
              </div>

              {/* Data Types */}
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-2">Data Types</legend>
                <div className="space-y-2">
                  {DATA_TYPE_OPTIONS.map(type => (
                    <label key={type} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dataTypes[type] || false}
                        onChange={() => handleDataTypeToggle(type)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{type}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Analytics Pipeline */}
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-2">Analytics Pipeline</legend>
                <p className="text-[10px] text-gray-400 mb-2">Select which analytics to run for synced interactions.</p>
                <div className="space-y-2">
                  {ANALYTICS_OPTIONS.map(opt => (
                    <label key={opt.key} className="flex items-start space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={analyticsConfig[opt.key] || false}
                        onChange={() => setAnalyticsConfig(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))}
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm text-gray-700">{opt.label}</span>
                        <p className="text-[10px] text-gray-400">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Save */}
              <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
                <button
                  onClick={handleSave}
                  disabled={saving || !displayName.trim()}
                  className="btn-primary text-sm w-full disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </PermissionGate>
            </div>
          </div>

          {/* Section C: Danger Zone — the whole section goes, not just the
              button, so a viewer isn't shown a warning about an action they
              cannot take. */}
          <PermissionGate permission={StandardPermission.Analytics.CONNECTORS}>
            <div className="border-t border-red-200 pt-4 mt-6">
              <h3 className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-3">Danger Zone</h3>
              <p className="text-xs text-gray-500 mb-3">
                Disconnecting will pause the connector and stop all data ingestion.
              </p>
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="btn-danger text-sm"
              >
                <XCircle size={14} className="inline mr-1.5 -mt-0.5" />
                Disconnect
              </button>
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* Disconnect confirmation */}
      <ConfirmDialog
        isOpen={showDisconnectConfirm}
        title="Disconnect Connector"
        message={`This will pause "${connector.display_name}" and stop all data ingestion. You can re-activate it later.`}
        confirmLabel="Disconnect"
        variant="danger"
        loading={disconnecting}
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectConfirm(false)}
      />
    </div>
  );
};

export default ConnectorSettingsDrawer;
